import { ProductImageComponent } from '../../shared/product-image/product-image.component'
import { Dialog } from '@angular/cdk/dialog'
import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { DialogService } from '../../../services/dialog.service'
import { ErrorService } from '../../../services/error.service'
import { ProductService } from '../../../services/product.service'
import { RouteService } from '../../../services/route.service'
import { SpinnerService } from '../../../services/spinner.service'
import { WindowService } from '../../../services/window.service'
import {
  ProductHighlights,
  ProductIngredient,
  ProductIngredientsResponse,
} from '../../../types/products'
import { firstValueFrom } from 'rxjs/internal/firstValueFrom'
import { ROUTES } from '../../../constants/routes'
import { ApiError } from '../../../types/apiError'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { MatDividerModule } from '@angular/material/divider'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatIconModule } from '@angular/material/icon'
import { MatToolbarModule } from '@angular/material/toolbar'
import { TranslationService } from '../../../services/translation.service'
import { SustainabilityHighlightsComponent } from '../sustainability-highlights/sustainability-highlights.component'
import { CANADIAN_PROVINCES } from '../../../constants/provinces'
import { LocationService } from '../../../services/location.service'
import { RecyclabilityLocation } from '../../../types/recyclability'

@Component({
  selector: 'app-product-ingredients',
  imports: [
    ProductImageComponent,
    MatDividerModule,
    MatTableModule,
    MatTooltipModule,
    MatIconModule,
    MatToolbarModule,
    SustainabilityHighlightsComponent,
  ],
  templateUrl: './product-ingredients.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-ingredients.component.scss',
})
export class ProductIngredientsComponent implements OnInit {
  route = inject(ActivatedRoute)
  dialog = inject(Dialog)
  dialogService = inject(DialogService)
  routeService = inject(RouteService)
  productService = inject(ProductService)
  spinnerService = inject(SpinnerService)
  errorService = inject(ErrorService)
  windowService = inject(WindowService)
  translationService = inject(TranslationService)
  locationService = inject(LocationService)

  product?: ProductIngredientsResponse

  chemistryUrl: string | undefined
  isMobileView: boolean = false

  displayedColumns: string[] = ['name', 'casNumbers', 'concentrationRange']
  mayContainDisplayedColumns: string[] = ['name', 'casNumbers']
  ingredientsDataSource = new MatTableDataSource<ProductIngredient>([])
  mayContainDataSource = new MatTableDataSource<ProductIngredient>([])
  doesNotContainSource = new MatTableDataSource<ProductIngredient>([])

  productHighlights?: ProductHighlights
  currentLocation: RecyclabilityLocation = CANADIAN_PROVINCES[0].name

  async ngOnInit(): Promise<void> {
    try {
      this.windowService.isMobileView$.subscribe((isMobileView) => {
        this.isMobileView = isMobileView
      })
      this.spinnerService.startSpinner()
      this.currentLocation =
        this.locationService.getLocation() ?? CANADIAN_PROVINCES[0].name

      const productId = this.route.parent?.snapshot.params['id']
      this.product = await firstValueFrom(
        this.productService.getProductIngredients(
          productId,
          this.currentLocation,
        ),
      )

      if (!this.product) {
        throw Error('Product ingredients must be provided')
      }

      const currentRoute = this.routeService.getProductViewRoutes(
        this.product.name,
        ROUTES.PRODUCT_INGREDIENTS.path,
        productId,
      )
      this.routeService.setRoute(currentRoute)

      this.productService.currentProductData.set({
        id: this.product.id,
        name: this.product.name,
        brand: this.product.brand,
      })

      this.ingredientsDataSource.data = this.product.productIngredients
      this.mayContainDataSource.data = this.product.mayContain
      this.doesNotContainSource.data = this.product.doesNotContain

      this.productHighlights = {
        fullIngredientDisclosure: this.product.fullIngredientDisclosure,
        recycledContentPercentage: this.product.recycledContentPercentage,
        provincialRecyclabilityLabels:
          this.product.provincialRecyclabilityLabels,
        ghsClassifications: this.product.ghsClassifications,
        countryRecyclability: this.product.countryRecyclability,
      }

      this.routeService.setRoute(currentRoute)
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }
}
